import { RESIDENT_LIBERTY_ROLES } from '../constants/residentConstants'
import type {
  DefaultProcessData,
  ResidentApplicationData,
  ResidentDefault,
  ResidentDefaultFormValues,
  ResidentDetailResponse,
} from '../types'
import { enrichCalculationSnapshotWithClientFallback } from './residentCalculationClientFallback'
import { apiPageForDetailTab } from './residentDecoupling'
import { scopePayloadByApiPage } from './detailPayloadScope'
import type { ResidentDetailTab } from '../types/domain'

/**
 * File upload form controls that the API never expects in the JSON payload —
 * they are sent via the upload endpoint instead. Mirrors Angular's
 * `delete requestData['eviction_lawsuit']` etc. in submitForm.
 */
/** Angular `firstmonth_months`: select values are 1–12 (labels shown zero-padded). */
export const FIRST_MONTH_NOT_PAID_MONTH_VALUES = Array.from({ length: 12 }, (_, i) =>
  String(i + 1),
)

export function formatFirstMonthNotPaidMonthLabel(month: string | number): string {
  const n = Number(month)
  if (!Number.isFinite(n) || n < 1 || n > 12) return String(month)
  return n < 10 ? `0${n}` : String(n)
}

/** Normalize month for form controls — unpadded `1`–`12` like Angular. */
export function normalizeFirstMonthNotPaidMonthValue(month: unknown): string {
  const raw = String(month ?? '').trim()
  if (!raw || !/^\d+$/.test(raw)) return ''
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 1 || n > 12) return raw
  return String(n)
}

const FILE_FIELDS = new Set([
  'eviction_lawsuit',
  'payment_ledger',
  'lease_agreement',
  'skip_claim_affidavit',
  'cdc_affidavit',
  'late_letter',
  'jurisdiction_eviction_updates',
  'other_supporting_documents',
])

/**
 * Fields that Angular's `getFilterValue` keeps in the request even when their
 * value is empty. This matters for clearing dates/notes on the server.
 */
const KEEP_EMPTY_FIELDS = new Set([
  'movein_date',
  'new_resident_lease_start_date',
  'adjusted_notice',
  'adjusted_reason',
  'loss_adjustment',
  'moveout_date',
])

/**
 * Mirrors Angular's amount handling in `validateData`: strip every char that
 * is not a digit or `.` and return as string. Angular preserves decimals
 * (e.g. `$1,240.49` -> `1240.49`).
 */
function normalizeAmount(value: unknown): string {
  if (value === null || value === undefined) return ''
  const stripped = String(value).replace(/[^0-9.]+/g, '')
  if (!stripped) return ''
  return stripped
}

/** Strip currency formatting; omit zero/blank amounts from API payloads. */
export function normalizePositiveAmountForApi(value: unknown): string {
  const stripped = normalizeAmount(value)
  if (!stripped) return ''
  const num = Number(stripped)
  if (!Number.isFinite(num) || num <= 0) return ''
  return stripped
}

function isReimbursementClaimSave(
  values: Pick<ResidentDefaultFormValues, 'rental_status'>,
  pageType: 'resident-detail' | 'default-detail',
  payloadPage?: unknown,
): boolean {
  if (pageType === 'default-detail' || payloadPage === 'default') return true
  return ['SKIPPED', 'EVICTION'].includes(String(values.rental_status ?? '').trim())
}

/**
 * Reimbursement 3.0: persist explicit `0` when deposit not collected so DB is not NULL.
 * Legacy resident-detail saves only send positive amounts (Liberty calculation path).
 */
export function applyUnusedSecurityDepositToApiPayload(
  payload: Record<string, unknown>,
  values: Pick<ResidentDefaultFormValues, 'unused_security_deposit' | 'rental_status'>,
  securityDepositCollected: boolean | null | undefined,
  pageType: 'resident-detail' | 'default-detail' = 'resident-detail',
): void {
  delete payload.unused_security_deposit
  if (isReimbursementClaimSave(values, pageType, payload.page)) {
    const collected = securityDepositCollected ?? false
    if (!collected) {
      payload.unused_security_deposit = '0'
      return
    }
    const securityDeposit = normalizePositiveAmountForApi(values.unused_security_deposit)
    if (securityDeposit) payload.unused_security_deposit = securityDeposit
    return
  }
  const securityDeposit = normalizePositiveAmountForApi(values.unused_security_deposit)
  if (securityDeposit) payload.unused_security_deposit = securityDeposit
}

/** Same rules as {@link applyUnusedSecurityDepositToApiPayload} for immediate multipart uploads. */
export function appendUnusedSecurityDepositToFormData(
  fd: FormData,
  values: Pick<ResidentDefaultFormValues, 'unused_security_deposit' | 'rental_status'>,
  securityDepositCollected: boolean | null | undefined,
): void {
  const payload: Record<string, unknown> = { page: 'default' }
  applyUnusedSecurityDepositToApiPayload(
    payload,
    values,
    securityDepositCollected,
    'default-detail',
  )
  if (payload.unused_security_deposit !== undefined) {
    fd.append('unused_security_deposit', String(payload.unused_security_deposit))
  }
}

function shouldKeepField(key: string, value: unknown): boolean {
  if (KEEP_EMPTY_FIELDS.has(key)) return true
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return true
}

function isBlankDisplayValue(value: unknown): boolean {
  if (value === undefined || value === null) return true
  const s = String(value).trim()
  return s === '' || s === '-' || s === '—'
}

/**
 * `defaultProcessData` from GET /api/default may be an object or a JSON string; it can also
 * appear on `resident_default` or alternate keys. Merge all non-empty sources (later entries
 * override), matching how Angular's `defaultProcessData` accumulates one shape for the tab.
 */
export function parseDefaultProcessDataRecord(detail?: ResidentDetailResponse | null): Record<string, unknown> {
  if (!detail) return {}

  const tryParse = (raw: unknown): Record<string, unknown> | null => {
    if (raw == null || raw === '') return null
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as unknown
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>
        }
        return null
      } catch {
        return null
      }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>
    return null
  }

  const root = detail as Record<string, unknown>
  const rd = detail.resident_default as Record<string, unknown> | undefined
  /** Low → high precedence so root `defaultProcessData` wins over nested fragments. */
  const candidates: unknown[] = [
    rd?.default_process_data,
    rd?.defaultProcessData,
    root.calculation_data,
    root.process_data,
    root.defaultCalculation,
    detail.default_process_data,
    detail.defaultProcessData,
  ]

  const merged: Record<string, unknown> = {}
  for (const c of candidates) {
    const rec = tryParse(c)
    if (rec != null && Object.keys(rec).length > 0) {
      Object.assign(merged, rec)
    }
  }

  fillMissingCalculationScalarsFromFlatDetail(merged, detail)
  return merged
}

/** Backend sometimes exposes calculation columns on GET /api/default root or `resident_default` instead of nested JSON. */
const CALCULATION_SCALAR_KEYS = [
  'lease_month',
  'notice_month_diff',
  'notice_month',
  'daily_rent',
  'gross_daily_rent',
  'loss_rent',
  'net_due',
  'net_amount',
  'lost_revenue',
  'months_lost',
  'max_movein_date',
  'max_reason',
  'rent_amount',
  'leaseMonth',
  'noticeMonthDiff',
  'dailyRent',
  'lossRent',
  'netDue',
  'lostRevenue',
  'monthsLost',
  'maxMoveinDate',
  'maxReason',
] as const

function isMergeEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (typeof value === 'string' && value.trim() === '') return true
  return false
}

function fillMissingCalculationScalarsFromFlatDetail(
  merged: Record<string, unknown>,
  detail?: ResidentDetailResponse | null,
): void {
  if (!detail) return
  const layers = [
    detail as Record<string, unknown>,
    (detail.resident_default || {}) as Record<string, unknown>,
  ]
  for (const key of CALCULATION_SCALAR_KEYS) {
    if (!isMergeEmpty(merged[key])) continue
    for (const layer of layers) {
      const v = layer[key]
      if (!isMergeEmpty(v)) {
        merged[key] = v
        break
      }
    }
  }
}

function firstDefined(...vals: unknown[]): unknown {
  for (const v of vals) {
    if (v === undefined || v === null) continue
    if (typeof v === 'string' && v.trim() === '') continue
    return v
  }
  return undefined
}

/**
 * Normalized calculation snapshot for the Calculation tab — same keys Angular binds in
 * `resident-edit-new` (`defaultProcessData.*`): snake_case from `validateData` / PHP plus
 * optional camelCase. Reimbursement rate falls back to application `rent_amount`.
 *
 * Angular also recomputes figures client-side in `calculationBaseonDate()`; the API usually
 * returns the persisted snapshot after save (`calculation_data`). This uses that snapshot.
 */
export function getCalculationTabSnapshot(
  detail: ResidentDetailResponse | null | undefined,
  applicationData: ResidentApplicationData | null | undefined,
  formValues?: ResidentDefaultFormValues,
): DefaultProcessData {
  const raw = parseDefaultProcessDataRecord(detail)
  const r = raw as Record<string, unknown>
  const snapshot = {
    ...raw,
    lease_month: firstDefined(r.lease_month, r.leaseMonth, r.lease_age) as DefaultProcessData['lease_month'],
    notice_month_diff: firstDefined(r.notice_month_diff, r.noticeMonthDiff) as DefaultProcessData['notice_month_diff'],
    daily_rent: firstDefined(r.daily_rent, r.dailyRent) as DefaultProcessData['daily_rent'],
    loss_rent: firstDefined(r.loss_rent, r.lossRent) as DefaultProcessData['loss_rent'],
    max_movein_date: firstDefined(r.max_movein_date, r.maxMoveinDate) as DefaultProcessData['max_movein_date'],
    max_reason: firstDefined(r.max_reason, r.maxReason) as DefaultProcessData['max_reason'],
    net_due: firstDefined(r.net_due, r.netDue) as DefaultProcessData['net_due'],
    net_amount: firstDefined(r.net_amount, r.netAmount) as DefaultProcessData['net_amount'],
    lost_revenue: firstDefined(r.lost_revenue, r.lostRevenue) as DefaultProcessData['lost_revenue'],
    months_lost: firstDefined(r.months_lost, r.monthsLost) as DefaultProcessData['months_lost'],
    rent_amount: firstDefined(r.rent_amount, r.rentAmount, applicationData?.rent_amount) as DefaultProcessData['rent_amount'],
  }
  if (!formValues) return snapshot
  return enrichCalculationSnapshotWithClientFallback(snapshot, detail, applicationData, formValues)
}

/**
 * Angular `adjustPayment` / `showPaymentPopup` / `getCalculationJSON`:
 * full `defaultProcessData` + form date merge + rent / first-month / timely / end date overlays.
 * Used as `adjustment_data` on Configure Payment, Adjust Payment, Deny/Reopen, Review Claim.
 */
export function buildAdjustmentDataFromForm(args: {
  snapshot: DefaultProcessData | Record<string, unknown> | null | undefined
  formValues: Pick<
    ResidentDefaultFormValues,
    | 'rental_status_date'
    | 'adjusted_notice'
    | 'movein_date'
    | 'new_resident_lease_start_date'
    | 'adjusted_reason'
    | 'timely'
    | 'adjust_max_movein_date'
    | 'first_month_not_paid_month'
    | 'first_month_not_paid_year'
    | 'loss_adjustment'
    | 'partial_payment_amount'
    | 'unused_security_deposit'
    | 'net_amount'
  > & {
    loss_adjusted_reason?: unknown
  }
  rentAmount?: number | string | null
}): Record<string, unknown> {
  const { snapshot, formValues, rentAmount } = args
  const base = { ...(snapshot ?? {}) } as Record<string, unknown>
  const firstMonth =
    formValues.first_month_not_paid_month && formValues.first_month_not_paid_year
      ? `${formValues.first_month_not_paid_month}/${formValues.first_month_not_paid_year}`
      : ''

  return {
    ...base,
    // Angular validateDates Object.assign into defaultProcessData
    rental_status_date: formValues.rental_status_date || '',
    adjusted_notice: formValues.adjusted_notice || '',
    movein_date: formValues.movein_date || '',
    new_resident_lease_start_date: formValues.new_resident_lease_start_date || '',
    adjusted_reason: formValues.adjusted_reason || '',
    loss_adjusted_reason: formValues.loss_adjusted_reason,
    // Live deduction / net fields from the form (Angular adjustment() writes these into defaultProcessData)
    loss_adjustment: formValues.loss_adjustment ?? base.loss_adjustment,
    partial_payment_amount: formValues.partial_payment_amount ?? base.partial_payment_amount,
    unused_security_deposit: formValues.unused_security_deposit ?? base.unused_security_deposit,
    // getCalculationJSON overlays
    rent_amount: rentAmount ?? base.rent_amount,
    first_month_not_paid: firstMonth,
    timely: formValues.timely,
    adjust_max_movein_date: formValues.adjust_max_movein_date,
    net_amount: formValues.net_amount || base.net_amount,
  }
}

/**
 * Lease age for the form: prefer the calculation snapshot (`defaultProcessData.lease_month`),
 * same source as the read-only figures on this tab, then `resident_default`.
 */
export function resolveLeaseMonthFromDetail(detail?: ResidentDetailResponse | null): string {
  if (!detail) return ''
  const calc = parseDefaultProcessDataRecord(detail)
  const rd = (detail.resident_default || {}) as Record<string, unknown>

  const pick = (value: unknown): string | undefined => {
    if (isBlankDisplayValue(value)) return undefined
    return String(value).trim()
  }

  const fromCalc =
    pick(calc.lease_month) ??
    pick(calc.leaseMonth) ??
    pick((calc as { lease_age?: unknown }).lease_age)
  if (fromCalc !== undefined) return fromCalc

  const fromRd = pick(rd.lease_month) ?? pick(rd.leaseMonth)
  if (fromRd !== undefined) return fromRd

  return ''
}

export function normalizeResidentDefaultFormValues(
  values: ResidentDefaultFormValues,
  contractStartDate?: string,
  contractEndDate?: string,
  unitNumber?: string | number,
  rentalStatus?: string,
  rentalProgress?: string,
  reasonForDefault?: string,
): ResidentDefaultFormValues {
  const normalized = { ...values }

  // Angular `rentalStatusChange`: the old `reason_for_default: 'OTHER'` default is
  // commented out — leave empty so the select shows "Select Reason".

  if ((normalized as Record<string, unknown>).special_note === 'COVID_19_YES') {
    normalized.covid_19 = '1'
  } else if ((normalized as Record<string, unknown>).special_note === 'COVID_19_NO') {
    normalized.covid_19 = '0'
  }

  const firstMonth = String((normalized as Record<string, unknown>).first_month_not_paid ?? '')
  if (firstMonth.includes('-')) {
    const parts = firstMonth.split('-')
    if (parts.length === 3) {
      normalized.first_month_not_paid_year = parts[0]
      normalized.first_month_not_paid_month = normalizeFirstMonthNotPaidMonthValue(parts[1])
    } else if (parts.length === 2) {
      const [a, b] = parts
      if (a.length === 4) {
        normalized.first_month_not_paid_year = a
        normalized.first_month_not_paid_month = b
      } else {
        normalized.first_month_not_paid_month = a
        normalized.first_month_not_paid_year = b
      }
    }
  } else if (!normalized.first_month_not_paid_year) {
    normalized.first_month_not_paid_year = String(new Date().getFullYear())
  }

  const rawMonth = normalizeFirstMonthNotPaidMonthValue(normalized.first_month_not_paid_month)
  if (rawMonth) {
    normalized.first_month_not_paid_month = rawMonth
  }

  if (!normalized.lease_start_date && contractStartDate) {
    normalized.lease_start_date = contractStartDate
  }
  if (!normalized.lease_end_date && contractEndDate) {
    normalized.lease_end_date = contractEndDate
  }
  if (!normalized.unit_number && unitNumber !== undefined && unitNumber !== null && String(unitNumber).trim() !== '') {
    normalized.unit_number = String(unitNumber)
  }
  if (!normalized.rental_status && rentalStatus) {
    normalized.rental_status = rentalStatus
  }
  if (!normalized.rental_progress && rentalProgress) {
    normalized.rental_progress = rentalProgress
  }
  if (!normalized.reason_for_default && reasonForDefault) {
    normalized.reason_for_default = reasonForDefault
  }

  return normalized
}

/**
 * Build the resident default save payload using the same transformation
 * sequence as Angular's `submitForm` -> `getFilterValue` -> `validateData`.
 *
 * Key behaviours intentionally matched:
 *  - `first_month_not_paid` is `YYYY-MM-DD` with day=01 (NOT `MM-YYYY`).
 *  - `is_final_ledger`: on a regular save it is always present (`'1'` or `''`); on a file upload it
 *    is added only when the ledger is confirmed final or the claim is locked (else omitted).
 *  - `loss_adjusted_reason` array is flattened to indexed keys
 *    `loss_adjusted_reason[0]`, `loss_adjusted_reason[1]`, ... or
 *    `loss_adjusted_reason[]: []` when empty.
 *  - File controls are stripped from the payload.
 *  - `message` is always deleted; `covid_19` is deleted for NOT_STARTED/ACTIVE.
 *  - `update_certificate=1` is added when `leaseChanged` is true.
 *  - `moveout_date` is included when transitioning to `EARLY_END`.
 *  - `claim_override=1` is added when set.
 *  - `send_to` is `property` for Liberty roles, `liberty` otherwise.
 */
export function buildResidentSavePayload({
  values,
  applicationId,
  roleName,
  leaseChanged = false,
  moveOutDate,
  rentalProgressChangedToEarlyEnd = false,
  claimOverride = false,
  /** Angular file-upload path: Yes on Payment Ledger dialog sets `is_final_ledger` for that POST. */
  ledgerUploadFinalIntent,
  /** True when this save carries a file (Angular's `fileUpload` branch); changes `is_final_ledger` rules. */
  hasFileUpload = false,
  /** `resident_default.claim_lock` — a ledger uploaded on a locked claim is always final. */
  claimLock = false,
  pageType = 'resident-detail',
  selectedTab,
  securityDepositCollected,
}: {
  values: ResidentDefaultFormValues
  applicationId?: string | number
  roleName: string
  leaseChanged?: boolean
  moveOutDate?: string
  rentalProgressChangedToEarlyEnd?: boolean
  claimOverride?: boolean
  ledgerUploadFinalIntent?: boolean | null
  hasFileUpload?: boolean
  claimLock?: boolean
  /** Route pagetype — used when `selectedTab` is omitted (legacy). */
  pageType?: 'resident-detail' | 'default-detail'
  /** Active detail tab — drives API `page` when set (resident vs reimbursement save). */
  selectedTab?: ResidentDetailTab
  /** Reimbursement Core tab — when false, API stores `unused_security_deposit` = 0. */
  securityDepositCollected?: boolean | null
}): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  Object.entries(values).forEach(([key, value]) => {
    if (FILE_FIELDS.has(key)) return
    if (key === 'loss_adjusted_reason' || key === 'lease_month') return
    if (key === 'first_month_not_paid_month' || key === 'first_month_not_paid_year') {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        payload[key] = String(value).trim()
      }
      return
    }
    if (
      key === 'unused_security_deposit' ||
      key === 'partial_payment_amount' ||
      key === 'loss_adjustment'
    ) {
      return
    }
    if (!shouldKeepField(key, value)) return
    payload[key] = typeof value === 'string' ? value.trim() : value
  })

  payload.page = selectedTab
    ? apiPageForDetailTab(selectedTab)
    : pageType === 'default-detail' ||
        ['SKIPPED', 'EVICTION'].includes(String(values.rental_status ?? '').trim())
      ? 'default'
      : 'resident'
  if (applicationId) payload.application_id = applicationId
  payload.send_to = RESIDENT_LIBERTY_ROLES.includes(roleName) ? 'property' : 'liberty'

  const month = String(values.first_month_not_paid_month || '').trim()
  const year = String(values.first_month_not_paid_year || '').trim()
  if (month && year) {
    payload.first_month_not_paid = `${year}-${month.padStart(2, '0')}-01`
  }

  const lossAdjustment = normalizeAmount(values.loss_adjustment)
  if (lossAdjustment) payload.loss_adjustment = lossAdjustment

  const partialPayment = normalizeAmount(values.partial_payment_amount)
  if (partialPayment) payload.partial_payment_amount = partialPayment

  applyUnusedSecurityDepositToApiPayload(payload, values, securityDepositCollected, pageType)

  if (hasFileUpload) {
    // Angular `fileUpload` branch: `is_final_ledger` is added ONLY when the claim is locked with a
    // ledger present (`payment_ledger && claim_lock`). For a lease/skip/eviction-only upload it is
    // omitted (not sent as '').
    if (isResidentFilePresent(values.payment_ledger) && claimLock) {
      payload.is_final_ledger = '1'
    }
  } else {
    // Angular regular-save branch: always present (`'1'` or `''`).
    payload.is_final_ledger = values.is_final_ledger ? '1' : ''
  }
  // Angular's `if (this.isFinalLedger) is_final_ledger='1'` sits OUTSIDE the branch, so the Payment
  // Ledger "Yes" answer forces final in both regular saves and file uploads.
  if (ledgerUploadFinalIntent === true) {
    payload.is_final_ledger = '1'
  }

  const lossAdjustedReason = (values as unknown as Record<string, unknown>).loss_adjusted_reason
  if (Array.isArray(lossAdjustedReason) && lossAdjustedReason.length) {
    lossAdjustedReason.forEach((entry, index) => {
      payload[`loss_adjusted_reason[${index}]`] = entry
    })
  } else {
    payload['loss_adjusted_reason[]'] = []
  }

  if (leaseChanged) {
    payload.update_certificate = '1'
  }

  if ((rentalProgressChangedToEarlyEnd || moveOutDate || values.moveout_date) && (moveOutDate || values.moveout_date)) {
    payload.moveout_date = moveOutDate || values.moveout_date
  }

  if (['NOT_STARTED', 'ACTIVE'].includes(String(values.rental_status))) {
    delete payload.covid_19
  }

  delete payload.message

  if (claimOverride) {
    payload.claim_override = '1'
  }

  const apiPage = String(payload.page ?? '')
  if (apiPage === 'default' || apiPage === 'resident') {
    scopePayloadByApiPage(payload, values, apiPage, {
      residentInfoTab: selectedTab === 'residentinfo',
    })
  }

  return payload
}

function appendPayloadEntriesToFormData(payload: Record<string, unknown>, fd: FormData): void {
  for (const [key, val] of Object.entries(payload)) {
    if (val === undefined || val === null) continue
    if (Array.isArray(val)) {
      fd.append(key, val.length === 0 ? '' : JSON.stringify(val))
      continue
    }
    if (typeof val === 'object') {
      fd.append(key, JSON.stringify(val))
      continue
    }
    fd.append(key, String(val))
  }
}

/** Append file parts like Angular `submitForm` + `fileLabel` merge (`eviction_lawsuit[n]`, single names for others). */
function appendResidentFileParts(values: ResidentDefaultFormValues, fd: FormData): void {
  for (const fieldName of FILE_FIELDS) {
    const raw = values[fieldName as keyof ResidentDefaultFormValues] as unknown
    if (raw == null) continue
    if (raw instanceof File) {
      fd.append(fieldName, raw)
    } else if (raw instanceof FileList || (typeof raw === 'object' && raw !== null && 'length' in raw)) {
      const list = Array.from(raw as ArrayLike<unknown>).filter((item): item is File => item instanceof File)
      if (list.length === 0) continue
      if (fieldName === 'eviction_lawsuit') {
        list.forEach((file, index) => {
          fd.append(`${fieldName}[${index}]`, file)
        })
      } else {
        fd.append(fieldName, list[0])
      }
    }
  }
}

export function residentSaveHasFiles(values: ResidentDefaultFormValues): boolean {
  for (const fieldName of FILE_FIELDS) {
    const raw = values[fieldName as keyof ResidentDefaultFormValues] as unknown
    if (raw instanceof File) return true
    if (raw instanceof FileList && raw.length > 0) return true
    if (typeof raw === 'object' && raw !== null && 'length' in raw && (raw as { length: number }).length > 0) return true
  }
  return false
}

/** True when a single file field currently holds a selected file. */
function isResidentFilePresent(raw: unknown): boolean {
  if (raw instanceof File) return true
  if (raw instanceof FileList) return raw.length > 0
  if (typeof raw === 'object' && raw !== null && 'length' in raw && (raw as { length: number }).length > 0) return true
  return false
}

/**
 * Angular `POST /api/default/:userId` with `{ upload: true }` sends multipart:
 * JSON fields from {@link buildResidentSavePayload} plus blob fields (`lease_agreement`, `payment_ledger`, `eviction_lawsuit[i]`, …).
 */
export function buildResidentSaveFormData(
  values: ResidentDefaultFormValues,
  payload: Record<string, unknown>,
): FormData {
  const fd = new FormData()
  appendPayloadEntriesToFormData(payload, fd)
  appendResidentFileParts(values, fd)
  return fd
}

export type ResidentImmediateUploadField =
  | 'lease_agreement'
  | 'original_lease_agreement'
  | 'payment_ledger'
  | 'original_payment_ledger'
  | 'final_payment_ledger'
  | 'eviction_lawsuit'
  | 'skip_claim_affidavit'
  | 'notice_to_vacate'
  | 'alternate_court_documents'
  | 'bankruptcy_petition'
  | 'appeal_document'
  | 'court_registry_rent_bond'
  | 'stipulation_agreement'
  | 'default_stipulation'
  | 'cdc_affidavit'
  | 'late_letter'
  | 'jurisdiction_eviction_updates'
  | 'other_supporting_documents'

/**
 * Angular `submitForm('fileUpload')` — multipart body with page, application_id,
 * optional `is_final_ledger`, selected file(s), and any dirty reimbursement fields
 * (first_month_not_paid_month/_year, movein_date, moveout_date, new_resident_lease_start_date,
 * covid_19, reason_for_default, timely, adjusted_notice, adjust_max_movein_date).
 * For EVICTION, always includes current `skip_claim_affidavit_required` so Yes/No survives reload.
 */
export function buildResidentImmediateFileUploadFormData({
  fieldName,
  files,
  applicationId,
  claimLock: _claimLock = false,
  formValues,
  dirtyFields,
  page = 'resident',
}: {
  fieldName: ResidentImmediateUploadField
  files: File[]
  applicationId?: string | number
  claimLock?: boolean
  formValues?: ResidentDefaultFormValues
  dirtyFields?: Partial<Record<keyof ResidentDefaultFormValues, boolean>>
  page?: 'default' | 'resident'
}): FormData {
  const fd = new FormData()
  fd.append('page', page)
  if (applicationId !== undefined && applicationId !== null && String(applicationId).trim() !== '') {
    fd.append('application_id', String(applicationId))
  }

  if (formValues && dirtyFields) {
    const checkFields = [
      'first_month_not_paid_month',
      'first_month_not_paid_year',
      'movein_date',
      'moveout_date',
      'new_resident_lease_start_date',
      'covid_19',
      'reason_for_default',
    ] as const

    checkFields.forEach((field) => {
      if (
        dirtyFields[field] &&
        formValues[field] !== undefined &&
        formValues[field] !== null &&
        String(formValues[field]).trim() !== ''
      ) {
        const val = String(formValues[field]).trim()
        fd.append(field, val)

        if (field === 'movein_date' && formValues.adjust_max_movein_date) {
          fd.append('adjust_max_movein_date', String(formValues.adjust_max_movein_date).trim())
        }
      }
    })

    // Always persist EVICTION "Skip Affidavit required?" with immediate uploads so
    // post-upload reload/reset does not flip Yes → No and mis-place the new document.
    if (String(formValues.rental_status ?? '') === 'EVICTION') {
      const skipRequired = String(formValues.skip_claim_affidavit_required ?? '').trim()
      if (skipRequired === '0' || skipRequired === '1') {
        fd.append('skip_claim_affidavit_required', skipRequired)
      }
    }

    const hasMonthOrYearDirty = Boolean(
      dirtyFields.first_month_not_paid_month || dirtyFields.first_month_not_paid_year,
    )
    if (
      formValues.first_month_not_paid_month &&
      formValues.first_month_not_paid_year &&
      hasMonthOrYearDirty
    ) {
      const y = String(formValues.first_month_not_paid_year).trim()
      const m = String(formValues.first_month_not_paid_month).padStart(2, '0')
      const formattedFirstMonth = `${y}-${m}-01`
      fd.append('first_month_not_paid', formattedFirstMonth)

      if (formValues.timely) {
        fd.append('timely', String(formValues.timely).trim())
        if (formValues.adjusted_notice) {
          fd.append('adjusted_notice', String(formValues.adjusted_notice).trim())
        }
        if (formValues.adjust_max_movein_date) {
          fd.append('adjust_max_movein_date', String(formValues.adjust_max_movein_date).trim())
        }
      }
    }
  }

  if (fieldName === 'eviction_lawsuit') {
    files.forEach((file, index) => {
      fd.append(`${fieldName}[${index}]`, file)
    })
  } else if (files[0]) {
    fd.append(fieldName, files[0])
  }
  return fd
}

function toYmd(value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toISOString().slice(0, 10)
}

/**
 * `calculation_data` for the settlement statement. Angular clones the live `defaultProcessData`
 * that `calculationBaseonDate()` / `adjustment()` keep recomputing in the browser, so the PDF
 * always matches the Calculation tab. GET `/api/default` never returns those figures, so the
 * persisted detail alone resolves to `{}` (stored by PHP as `calculation_data: []`) and the
 * settlement section of the PDF renders blank.
 */
function buildSettlementCalculationData(
  residentDetail: ResidentDetailResponse | null,
  applicationData: ResidentApplicationData | null | undefined,
  values: ResidentDefaultFormValues,
): Record<string, unknown> {
  const snapshot = getCalculationTabSnapshot(residentDetail, applicationData, values) as Record<
    string,
    unknown
  >

  const calculation_data: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(snapshot)) {
    if (value === undefined || value === null) continue
    calculation_data[key] = value
  }

  for (const dateKey of ['max_movein_date', 'adjust_max_movein_date'] as const) {
    const value = calculation_data[dateKey]
    if (value != null && value !== '') calculation_data[dateKey] = toYmd(value)
  }

  if (isBlankDisplayValue(calculation_data.lease_month)) {
    const leaseMonthRaw = String(values.lease_month ?? '').trim()
    if (leaseMonthRaw !== '') {
      const leaseNum = Number(leaseMonthRaw.replace(/[^0-9.-]/g, ''))
      calculation_data.lease_month = Number.isFinite(leaseNum) ? leaseNum : values.lease_month
    }
  }

  return calculation_data
}

/**
 * Body for `POST /api/default_payment_statement/:id`. Mirrors Angular's
 * `defaultPaymentStatement`: `{ default_data, calculation_data }` where
 * `calculation_data` is a clone of `defaultProcessData` with
 * `max_movein_date` normalized to `YYYY-MM-DD`.
 */
export function buildSettlementGeneratePayload({
  values,
  residentDetail,
  applicationData,
  applicationId,
  roleName,
  leaseChanged = false,
  moveOutDate,
  rentalProgressChangedToEarlyEnd = false,
  selectedTab = 'calculation',
}: {
  values: ResidentDefaultFormValues
  residentDetail: ResidentDetailResponse | null
  applicationData?: ResidentApplicationData | null
  applicationId?: string | number
  roleName: string
  leaseChanged?: boolean
  moveOutDate?: string
  rentalProgressChangedToEarlyEnd?: boolean
  selectedTab?: ResidentDetailTab
}): Record<string, unknown> {
  const default_data = buildResidentSavePayload({
    values,
    applicationId,
    roleName,
    leaseChanged,
    moveOutDate,
    rentalProgressChangedToEarlyEnd,
    selectedTab,
  })
  const calculation_data = buildSettlementCalculationData(residentDetail, applicationData, values)
  return { default_data, calculation_data }
}

/**
 * Mirrors Angular's `checkisLeaseChange`. Returns `true` when the lease dates
 * in the form differ from the persisted record.
 */
export function isLeaseChanged(
  values: Pick<ResidentDefaultFormValues, 'lease_start_date' | 'lease_end_date'>,
  residentDefault?: ResidentDefault,
): boolean {
  const savedStart = residentDefault?.lease_start_date as string | undefined
  const savedEnd = residentDefault?.lease_end_date as string | undefined
  if (!savedStart || !savedEnd) return false
  const startChanged =
    String(values.lease_start_date || '').slice(0, 10) !== String(savedStart).slice(0, 10)
  const endChanged =
    String(values.lease_end_date || '').slice(0, 10) !== String(savedEnd).slice(0, 10)
  return startChanged || endChanged
}
